import React, { useRef } from "react";
import { useNavigate, useOutletContext, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { IMG, LOGO, ADDRESS } from "../lib/images";
import { MaskLines, Reveal } from "../components/site/Reveal";
import { RibbonMarquee } from "../components/site/Marquee";
import { GoogleReviews } from "../components/site/GoogleReviews";
import { useSeo } from "../components/site/useSeo";

const CATS = [
  { title: "Farmhouse Breakfast", copy: "Scratch-made breakfasts served daily — chicken fried steak & eggs, buttermilk pancakes, and biscuits & sausage gravy.", img: IMG.breakfast, span: "md:col-span-2 md:row-span-2" },
  { title: "Southern Lunch Favorites", copy: "Homestyle plates like chicken fried steak, hamburger steak, and pulled pork — served with homemade sides.", img: IMG.cfs, span: "md:col-span-2" },
  { title: "Burgers & Sandwiches", copy: "Fresh beef burgers and stacked sandwiches, all served with fries.", img: IMG.burger, span: "md:col-span-1" },
  { title: "Something Sweet", copy: "Homemade Southern desserts — banana pudding and chocolate cake worth saving room for.", img: IMG.dessert, span: "md:col-span-1" },
];

const CHAPTERS = [
  { n: "01", t: "Real Ingredients", c: "Around here, \"homemade\" still means something. We start from scratch with real ingredients and time-honored recipes." },
  { n: "02", t: "Cast-Iron Cooking", c: "We take inspiration from Southern kitchens, Louisiana cooking, family recipes, cast-iron skillets, and Sunday suppers." },
  { n: "03", t: "Genuine Hospitality", c: "No pretension. Just good Southern food and genuine hospitality — the meals people remember long after the plates are cleared." },
];

const directions = `https://www.google.com/maps/dir/?api=1&destination=${ADDRESS.mapsQuery}`;

export default function Home() {
  const navigate = useNavigate();
  const { settings } = useOutletContext();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useSeo({
    title: "Hackberry Home | Southern Scratch Made Kitchen — Ben Wheeler, TX",
    description: "Scratch-made Southern favorites with a little Louisiana soul, served in Ben Wheeler, Texas. Cajun food, fried chicken & homemade comfort food in East Texas.",
    path: "/",
  });

  const hours = settings?.hours || {};

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden grain" data-testid="hero">
        <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
          <img src={IMG.heroFeast} alt="Southern feast on a rustic table" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-hb-black/65" />
          <div className="absolute inset-0 bg-gradient-to-t from-hb-black via-hb-black/20 to-hb-black/60" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <motion.img
            src={LOGO} alt="Hackberry Home logo"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="h-40 w-40 md:h-56 md:w-56 object-contain logo-invert mb-8 mx-auto"
          />
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }} className="eyebrow text-hb-brass mb-5">
            Ben Wheeler · East Texas
          </motion.p>
          <h1 className="font-serif text-hb-cream text-5xl sm:text-6xl lg:text-8xl leading-[0.95] max-w-4xl">
            <MaskLines start={0.35} lines={["Southern Food Made", "the Way It Should Be."]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-7 text-hb-muted text-base md:text-lg max-w-xl leading-relaxed"
          >
            Scratch-made Southern favorites with a little Louisiana soul, served right here in Ben Wheeler, Texas.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button onClick={() => navigate("/menu")} data-testid="hero-view-menu" className="btn-brass eyebrow px-9 py-4 flex items-center justify-center gap-2">
              View Our Menu <ArrowRight size={16} />
            </button>
            <a href={directions} target="_blank" rel="noreferrer" data-testid="hero-directions" className="btn-outline-cream eyebrow px-9 py-4 flex items-center justify-center gap-2">
              <MapPin size={16} /> Get Directions
            </a>
          </motion.div>
        </motion.div>
      </section>

      <RibbonMarquee dark />

      {/* WELCOME */}
      <section className="bg-hb-black relative grain py-24 md:py-36" data-testid="welcome-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center relative z-10">
          <Reveal>
            <div className="relative">
              <div className="border border-hb-brass/40 p-3">
                <img src={IMG.biscuits} alt="Homemade buttermilk biscuits" className="w-full aspect-[4/5] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-4 md:-right-8 bg-hb-brass text-hb-black px-7 py-5">
                <p className="font-serif text-4xl leading-none">Est.</p>
                <p className="eyebrow text-[0.6rem] mt-1">Ben Wheeler, TX</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><p className="eyebrow text-hb-brass mb-6">Welcome Home</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl md:text-6xl text-hb-cream leading-[1] mb-8">Good food starts<br />from scratch.</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="space-y-5 text-hb-muted leading-relaxed max-w-lg">
                <p>We're bringing the flavors of a Southern family kitchen to Ben Wheeler — from comforting homemade favorites to the bold Cajun flavors of Louisiana.</p>
                <p>Our kitchen is built around real ingredients, time-honored recipes, generous portions, and the kind of food that makes you want to sit down and stay awhile.</p>
                <p>Whether you're joining us for a familiar Southern favorite or something with a little Cajun kick, there's always a place for you at our table.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FROM OUR KITCHEN */}
      <section className="bg-hb-surface relative py-24 md:py-32" data-testid="from-our-kitchen">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal><p className="eyebrow text-hb-brass mb-4">From Our Kitchen</p></Reveal>
              <Reveal delay={0.05}><h2 className="font-serif text-4xl md:text-6xl text-hb-cream leading-none">A Taste of the South</h2></Reveal>
            </div>
            <Reveal delay={0.1}>
              <Link to="/menu" data-testid="kitchen-explore-menu" className="group inline-flex items-center gap-2 eyebrow text-hb-cream hover:text-hb-brass transition-colors">
                Explore the Menu <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[220px] gap-4">
            {CATS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06} className={c.span}>
                <div className="group relative h-full min-h-[280px] overflow-hidden border border-hb-line" data-testid={`kitchen-card-${i}`}>
                  <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-hb-black via-hb-black/30 to-transparent" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end">
                    <h3 className="font-serif text-3xl md:text-4xl text-hb-cream">{c.title}</h3>
                    <p className="text-hb-muted text-sm mt-2 max-w-xs opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">{c.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="relative py-28 md:py-40 overflow-hidden" data-testid="brand-story">
        <img src={IMG.kitchen} alt="Cast iron cooking" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-hb-black/85" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-10">
          <Reveal><p className="eyebrow text-hb-brass mb-6">Our Philosophy</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl md:text-7xl text-hb-cream leading-[1.02] max-w-3xl mb-16">Made From Scratch.<br /><span className="italic text-hb-brass">Served From the Heart.</span></h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 border-t border-hb-line pt-12">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.n} delay={i * 0.1}>
                <div>
                  <p className="font-serif text-5xl text-hb-brass/60 mb-4">{ch.n}</p>
                  <h3 className="font-serif text-2xl text-hb-cream mb-3">{ch.t}</h3>
                  <p className="text-hb-muted text-sm leading-relaxed">{ch.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="bg-hb-black relative grain py-24 md:py-32" data-testid="location-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch relative z-10">
          <div className="flex flex-col justify-center">
            <Reveal><p className="eyebrow text-hb-brass mb-5">Come See Us</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-serif text-4xl md:text-6xl text-hb-cream leading-none mb-8">Find Your Way<br />to the Table</h2></Reveal>
            <Reveal delay={0.1}>
              <div className="text-hb-cream text-lg mb-8">
                <p className="font-serif text-2xl">Hackberry Home</p>
                <p className="text-hb-muted mt-2">{ADDRESS.line1}</p>
                <p className="text-hb-muted">{ADDRESS.cityStateZip}</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-10 max-w-sm">
                {Object.entries(hours).map(([day, val]) => (
                  <div key={day} className="flex justify-between border-b border-hb-line py-2 text-sm">
                    <span className="text-hb-muted">{day.slice(0, 3)}</span>
                    <span className="text-hb-cream">{val}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <a href={directions} target="_blank" rel="noreferrer" data-testid="location-directions" className="btn-brass eyebrow px-9 py-4 inline-flex items-center gap-2 w-fit">
                <MapPin size={16} /> Get Directions
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="border border-hb-line h-full min-h-[380px]">
              <iframe
                title="Hackberry Home location map"
                src={`https://www.google.com/maps?q=${ADDRESS.mapsQuery}&output=embed`}
                className="w-full h-full min-h-[380px] grayscale-[30%] contrast-[1.1]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <GoogleReviews />

      {/* INSTAGRAM / SOCIAL */}
      <section className="bg-hb-black relative grain py-24 md:py-32" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="text-center mb-14">
            <Reveal><p className="eyebrow text-hb-brass mb-5">Follow Along From the Kitchen</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-serif text-4xl md:text-6xl text-hb-cream leading-none">Behind the Skillet</h2></Reveal>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[IMG.cfs, IMG.burger, IMG.sides, IMG.dessert].map((img, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <a href={settings?.instagram || settings?.facebook || "#"} target="_blank" rel="noreferrer" className="group block relative aspect-square overflow-hidden border border-hb-line">
                  <img src={img} alt="Hackberry Home food" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-hb-black/0 group-hover:bg-hb-black/40 transition-colors" />
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="text-center mt-10">
              <a href={settings?.facebook || "#"} target="_blank" rel="noreferrer" className="btn-outline-cream eyebrow px-8 py-4 inline-flex">Follow Us</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
